import React, { createContext, useContext, useState, useEffect } from 'react';
import { groupStudentsByGroup, distributeGroupsByDays, parseExcelFile } from '../utils/excelParser';
import api from '../services/api';

const AppContext = createContext(undefined);

export const AppProvider = ({ children }) => {
    const [students, setStudents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [days, setDays] = useState([]);
    const [studyTypeFilter, setStudyTypeFilter] = useState('kunduzgi');
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
    const [userRole, setUserRole] = useState(localStorage.getItem('role'));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setIsAuthenticated(true);
            fetchStudents();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (students.length > 0) {
            const filteredStudents = students.filter(s => s.studyType === studyTypeFilter);
            const groupedStudents = groupStudentsByGroup(filteredStudents);
            setGroups(groupedStudents);
            const distributedDays = distributeGroupsByDays(groupedStudents);
            const calendarData = distributedDays.map((day, index) => ({
                id: index,
                date: new Date(Date.now() + index * 24 * 60 * 60 * 1000),
                groups: day.groups,
                totalStudents: day.totalStudents,
            }));
            setDays(calendarData);
        } else {
            setGroups([]);
            setDays([]);
        }
    }, [students, studyTypeFilter]);

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const response = await api.get('/students/');
            setStudents(response.data);
        } catch (error) {
            console.error('Failed to fetch students', error);
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        const response = await api.post('/login', { username, password });
        const { token, role } = response.data;
        if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('role', role);
            setUserRole(role);
            setIsAuthenticated(true);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        setIsAuthenticated(false);
        setUserRole(null);
        setStudents([]);
    };

    const handleFileUpload = async (event) => {
        if (userRole === 'read_only') {
            alert('У вас нет прав для выполнения этого действия.');
            return;
        }
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            setLoading(true);
            const parsedStudents = await parseExcelFile(file);
            await api.post('/students/import', { students: parsedStudents });
            await fetchStudents();
        } catch (error) {
            console.error('Error importing file:', error);
            alert('Ошибка при импорте файла.');
        } finally {
            setLoading(false);
        }
    };

    const confirmStudent = async (studentId) => {
        if (userRole === 'read_only') return;
        try {
            setStudents(prev => prev.map(s =>
                s.id === studentId
                    ? { ...s, confirmed: true, confirmedDate: new Date().toISOString() }
                    : s
            ));
            await api.post(`/students/${studentId}/confirm/`);
        } catch (error) {
            console.error('Failed to confirm student', error);
            alert('Ошибка при подтверждении.');
            fetchStudents();
        }
    };

    const unconfirmStudent = async (studentId) => {
        if (userRole === 'read_only') return;
        try {
            setStudents(prev => prev.map(s =>
                s.id === studentId
                    ? { ...s, confirmed: false, confirmedDate: null }
                    : s
            ));
            await api.post(`/students/${studentId}/unconfirm/`);
        } catch (error) {
            console.error('Failed to unconfirm student', error);
            alert('Ошибка при отмене подтверждения.');
            fetchStudents();
        }
    };

    const resetData = async () => {
        if (userRole === 'read_only') return;
        if (window.confirm('Вы уверены? Все данные будут удалены безвозвратно.')) {
            try {
                await api.post('/reset');
                setStudents([]);
            } catch (error) {
                console.error('Reset failed', error);
                alert('Ошибка сброса данных (возможно недостаточно прав)');
            }
        }
    };

    const getDashboardStats = () => {
        const filteredStudents = students.filter(s => s.studyType === studyTypeFilter);
        const totalStudents = filteredStudents.length;
        const confirmedStudents = filteredStudents.filter(s => s.confirmed).length;
        const todayGroups = days[0]?.groups || [];
        const todayTarget = todayGroups.reduce((sum, g) => sum + g.totalCount, 0);
        const todayConfirmed = todayGroups.reduce((sum, g) => sum + g.confirmedCount, 0);

        return {
            totalStudents,
            confirmedStudents,
            unconfirmedStudents: totalStudents - confirmedStudents,
            todayTarget,
            todayConfirmed,
        };
    };

    const getDayGroups = (dayIndex) => days[dayIndex]?.groups || [];
    const getGroup = (groupId) => groups.find(g => g.id === groupId);

    const getUploadStatistics = async () => {
        try {
            const response = await api.get('/students/upload_statistics/');
            return response.data;
        } catch (error) {
            console.error('Failed to fetch statistics', error);
            return [];
        }
    };

    return (
        <AppContext.Provider value={{
            students, groups, days, studyTypeFilter, setStudyTypeFilter,
            handleFileUpload, confirmStudent, unconfirmStudent, resetData, getDashboardStats,
            getDayGroups, getGroup, isAuthenticated, userRole, login, logout, loading, getUploadStatistics
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) throw new Error('useApp must be used within AppProvider');
    return context;
};
